import React from 'react';
import { Box, BoxProps } from '@mui/material';

interface SparkEditableTextProps {
  initialText: string;
  onTextChange: (text: string) => void;
  containerStyles?: BoxProps['sx'];
  disabled?: boolean;
  placeholder?: string;
  containerProps?: Omit<BoxProps, 'sx'>;
}

const SparkEditableText = ({
  initialText,
  onTextChange,
  containerStyles = {},
  disabled = false,
  placeholder = 'Untitled',
  containerProps,
}: SparkEditableTextProps) => {
  const updateText = (event: React.FocusEvent<HTMLDivElement>) => {
    const currentText = event.currentTarget.textContent;

    if (currentText && currentText !== initialText) {
      onTextChange(currentText);
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.stopPropagation();
    }
    if ((event.key === 'a' || event.key === 'A') && event.metaKey) {
      event.stopPropagation();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const currentText = event.currentTarget.textContent;

      if (currentText && currentText !== initialText) {
        onTextChange(currentText);
      }
      event.currentTarget.blur();
    }
  };

  // using survey js classes for simplicity
  return (
    <Box
      display='flex'
      alignItems='center'
      gap={1}
      sx={{
        color: 'black',
        whiteSpace: 'nowrap',
        paddingY: 1.1,
        '& span': {
          fontFamily: 'Mont-SemiBold',
        },
        ...containerStyles,
        ...(disabled ? { pointerEvents: 'none' } : {}),
      }}
      {...containerProps}
    >
      <Box className='svc-string-editor'>
        <Box className='svc-string-editor__content' component='span'>
          <div className='svc-string-editor__border svc-string-editor__border--hover'></div>
          <div className='svc-string-editor__border svc-string-editor__border--focus'></div>
          <Box className='svc-string-editor__input' component='span'>
            <Box
              component='span'
              role='textbox'
              className='sv-string-editor nodrag'
              spellCheck
              aria-label='content editable'
              tabIndex={-1}
              contentEditable={!disabled}
              data-placeholder={placeholder}
              suppressContentEditableWarning
              onBlur={updateText}
              onInput={(e) => {
                if (e.currentTarget.textContent?.trim() === '') {
                  e.currentTarget.innerHTML = '';
                }
              }}
              onKeyDown={handleKeyDown}
              dangerouslySetInnerHTML={{ __html: initialText || '' }} // Ensures proper empty state
              sx={{
                whiteSpace: 'nowrap !important',
                '&:empty:before': {
                  content: 'attr(data-placeholder)',
                  color: 'var(--color-label)',
                  pointerEvents: 'none',
                },
                minWidth: 20,
              }}
            />

            <div className='svc-string-editor__controls'></div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SparkEditableText;
